import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { get } from 'lodash';
import { useAppDispatch, useAppSelector } from 'app/hooks';

// components
import { Card } from '@tcc/crate';
import Timestamp from 'manage/Timestamp';
import CheckboxRadio from 'common/CheckboxRadio';
import ManagementZonesThumbnail from 'manual/ManagementZonesThumbnail';
import ImportedMapsUploadCard from 'manual/ImportedMapsUploadCard';

// constants
import { managementZonesTypes } from 'constants/scripts';

// utils
import { getMessage } from 'util/locale';

// selectors
import {
  getSelectedManagementZonesId,
  getSelectedManagementZonesType,
  getSortedPreviousPrescriptions
} from './selectors';

// actions
import { selectManualScript } from 'manual';

const SelectScript = () => {
  const dispatch = useAppDispatch();
  const scripts = useAppSelector(getSortedPreviousPrescriptions);
  const selectedScript = useAppSelector(getSelectedManagementZonesId);
  const selectedManagementZonesType = useAppSelector(getSelectedManagementZonesType);
  const disableNotPolygonalMaps = useAppSelector((state) =>
    state.launchDarkly.scriptCreatorDisableNotPolygonalImportedMaps);

  const isImportedScript = selectedManagementZonesType === managementZonesTypes.IMPORTED_MAPS_PARENT;
  const baseString = 'vra-management-zones';
  const onSelect = useCallback((isDisabled, id) => {
    if (!isDisabled) {
      dispatch(selectManualScript(id));
    }
  }, [dispatch]);

  const zones = scripts.map((script) => {
    const {
      fertilityActivity,
      applicationActivity,
      id,
      managementZonesType,
      lastUpdated,
      importedDate,
      primary,
      zones,
      polygonal
    } = script;

    let headerText;
    const primaryText = primary ? '--primary' : '';

    // imported scripts have importedDate property, all other scripts have lastUpdated
    const timeStamp = lastUpdated ? lastUpdated : importedDate;

    switch (managementZonesType) {
      case managementZonesTypes.FERTILITY:
        headerText = getMessage(
          `title-text__select-script-quick-view--fertility--${fertilityActivity}${primaryText}`,
          { ...script }
        );
        break;
      case managementZonesTypes.PLANTING:
        headerText = getMessage(`title-text__select-script-quick-view--planting${primaryText}`, script);
        break;
      case managementZonesTypes.IMPORTED_MAPS:
        headerText = get(script, 'name');
        break;
      default:
        headerText = getMessage(
          `title-text__select-script-quick-view--application--${applicationActivity}${primaryText}`,
          script
        );
        break;
    }

    const isSelected = id === selectedScript;
    // Not polygonal maps cannot be used to generate zones
    const isDisabled = disableNotPolygonalMaps && isImportedScript && !polygonal;
    const cardBaseString = 'vra-select-script-quick-view';
    const cardClassString = classNames({
      [`${cardBaseString}`]: true,
      isSelected,
      isDisabled
    });
    return (
      <li key={id}>
        <Card
          className={cardClassString}
        >
          <div onClick={() => onSelect(isDisabled,id)}>
            <ManagementZonesThumbnail
              type={managementZonesType}
              zones={zones}
            />
            <header>
              <CheckboxRadio
                isChecked={isSelected}
                labelText={headerText}
                name='management-zones'
                onChange={() => onSelect(isDisabled,id)}
                type='radio'
                disabled={isDisabled}
              />
              <p>
                <Timestamp timestamp={timeStamp} />
              </p>
            </header>
          </div>
        </Card>
      </li>
    );
  });

  if (isImportedScript) {
    const uploadCard = (
      <li
        className={`${baseString}__zones-quick-view`}
        key="imported-maps-upload">
        <ImportedMapsUploadCard />
      </li>
    );
    zones.push(uploadCard);
  }

  const isAnyMapNotPolygonal = useMemo(() => scripts.some(({ polygonal }) => !polygonal), [scripts]);
  const showDisabledImportedMapsMessage = disableNotPolygonalMaps && isImportedScript && isAnyMapNotPolygonal;
  const sectionBaseString = 'vra-select-script-container';
  const sectionClassString = classNames({
    [`${sectionBaseString}`]: true,
    'vra-create-script-screen': true
  });
  return (
    <section className={sectionClassString}>
      {showDisabledImportedMapsMessage && <p>{getMessage('text__imported-maps__disabled-explanation')}</p>}
      <ul className="vra-select-script-list">
        {zones}
      </ul>
    </section>
  );
};

export default SelectScript;
